import {
	HttpClient,
	HttpResponse
}                             from '@angular/common/http';
import { Injectable }         from '@angular/core';
import {
	ArchivingExport,
	CreateArchivingExportBody
}                             from '@app/modules/archiving/models/archiving-export.model';
import { LoggerService }      from '@app/shared/services/logger.service';
import { AbstractApiService } from '@core/http/abstract-api.service';
import { SanitizerService }   from '@core/http/sanitizer.service';
import { TransitionService }  from '@core/http/transition.service';
import {
	IArchivingExportHttpParams,
	RequestActionEnum,
	RequestCompleteEntityState,
	RequestCompleteListState
}                             from '@core/interfaces/api.interface';
import { IList }              from '@core/interfaces/list-response.interfaces';
import { ModelTypeEnum }      from '@models/transition.model';
import {
	ClsCreateExportBodyDTO,
	CreateExportBodyDTO,
	CreateExportRequestParams,
	ExportDTO,
	ExportsApiClient,
	ExtendExportRequestParams,
	ListExportsRequestParams,
	ReadExportRequestParams
}                             from '@openApiGenerated/archiving-export-api';
import { Observable }         from 'rxjs';

@Injectable( {
	providedIn: 'root'
} )
export class ArchivingExportApiService extends AbstractApiService {

	constructor(
		protected http: HttpClient,
		protected logger: LoggerService,
		private readonly exportsApiClient: ExportsApiClient,
		private readonly sanitize: SanitizerService,
		private readonly transition: TransitionService
	) {
		super();
		this.http = http;
	}


	// SANITIZER ######################################
	private sanitizeApiResponse( entity: ArchivingExport, context = this ): void {
		if ( entity instanceof ArchivingExport ) {
			entity.created = context.sanitize.date<Date>( entity.created, ModelTypeEnum.TMO, Date );
			entity.expiration = context.sanitize.date<Date>( entity.expiration, ModelTypeEnum.TMO, Date );
			entity.lastModified = context.sanitize.date<Date>( entity.lastModified, ModelTypeEnum.TMO, Date );
		}
	}

	private sanitizeForApiRequest( dto: CreateExportBodyDTO, context = this ): void {
		if ( dto instanceof ClsCreateExportBodyDTO ) {
		}
	}


	// SPECIFIC MAPPER ################################

	// LIST TYPE REQUESTS #############################
	public search( params?: IArchivingExportHttpParams, throwError = false ): Observable<RequestCompleteListState<ArchivingExport>> {
		this.logger.debug( 'pre-list call' );

		const requestParam: ListExportsRequestParams = {
			businessPremisesId: params?.businessPremisesId,
			createdDateFrom: params?.createdDateFrom,
			createdDateTo: params?.createdDateTo,
			status: params?.status,
			sort: params?.sort,
			pageSize: params?.paging.pageSize,
			pageNumber: params?.paging.pageNumber
		};

		const req$ = this.exportsApiClient.listExports( requestParam );

		return this.transition.handleListRequestResponse<ExportDTO, ArchivingExport>(
			req$ as Observable<IList<ExportDTO>>,
			ArchivingExport,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	// ENTITY TYPE REQUESTS ###########################
	public read( id: string, withFullResponse: boolean = true, throwError = false ): Observable<RequestCompleteEntityState<ArchivingExport>> {
		this.logger.debug( 'pre-read call: ', id );
		const requestParam: ReadExportRequestParams = { exportId: id };

		const req$ = this.exportsApiClient.readExport( requestParam, withFullResponse ? 'response' : undefined );
		return this.transition.handleEntityRequestResponse<ExportDTO | HttpResponse<ExportDTO>, ArchivingExport>(
			req$,
			RequestActionEnum.READ,
			ArchivingExport,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public create(
		exportBody: CreateArchivingExportBody, businessPremisesId: string, throwError = false
	): Observable<RequestCompleteEntityState<ArchivingExport>> {
		this.logger.debug( 'pre-create call: ', { ...exportBody } );
		const createExportBodyDTO = this.transition.prepareEntityRequest<CreateArchivingExportBody, CreateExportBodyDTO>(
			exportBody, ClsCreateExportBodyDTO, this, this.sanitizeForApiRequest
		);

		const requestParam: CreateExportRequestParams = {
			createExportBodyDTO,
			businessPremisesId
		};

		const req$ = this.exportsApiClient.createExport( requestParam );
		return this.transition.handleEntityRequestResponse<ExportDTO, ArchivingExport>(
			req$,
			RequestActionEnum.CREATE,
			ArchivingExport,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public extendExport( archivingExportId: string, throwError = false ): Observable<RequestCompleteEntityState<ArchivingExport>> {
		this.logger.debug( 'pre-extend call: ', archivingExportId );

		const requestParam: ExtendExportRequestParams = {
			exportId: archivingExportId
		};

		const req$ = this.exportsApiClient.extendExport( requestParam );
		return this.transition.handleEntityRequestResponse<ExportDTO, ArchivingExport>(
			req$,
			RequestActionEnum.UPDATE,
			ArchivingExport,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}
}
